import { useCallback } from 'react';
import { useOnboarding } from './useOnboarding';
import { getActiveNudges, getActiveTourStep, getViajeFases } from '../lib/nudges';
import { trackEvent } from '../lib/eventTracking';

/**
 * useNudges — fachada sobre useOnboarding + motor (src/lib/nudges.js).
 *
 * Consumer típico (App.jsx):
 *   const nudges = useNudges(userId, profile);
 *   nudges.getActiveForScope('home');
 *   nudges.getTourStep();
 *   nudges.getFases();
 *   nudges.executeAction(nudge, setAppState);
 *
 * También reexpone los mutadores de useOnboarding (completeTour, skipTour,
 * markPrimerTurnoPreparado, resetTour) para que el consumer no tenga que
 * llamar a useOnboarding por separado y mantener una sola instancia del
 * estado.
 */
export function useNudges(userId, profile) {
  const onboarding = useOnboarding(userId, profile);
  const { state, dismissNudge } = onboarding;

  const getActiveForScope = useCallback(
    (scope) => getActiveNudges(scope, state),
    [state],
  );

  const getTourStep = useCallback(() => getActiveTourStep(state), [state]);
  const getFases = useCallback(() => getViajeFases(state), [state]);

  const dismiss = useCallback((nudgeId) => dismissNudge(nudgeId), [dismissNudge]);

  // executeAction: dispara nudge_clickeado y llama al navigator del consumer
  // con el destino. El consumer (App.jsx) mapea appState → setAppState.
  // Si en el futuro hay más tipos de action, este es el único lugar donde
  // se decide la disambiguación.
  const executeAction = useCallback((nudge, navigator) => {
    if (!nudge?.action) return;
    trackEvent('nudge_clickeado', {
      modulo: nudge.scope === 'tour' ? 'onboarding' : nudge.scope,
      payload: { nudge_id: nudge.id, scope: nudge.scope },
    });
    if (nudge.action.type === 'goto' && typeof navigator === 'function') {
      navigator(nudge.action.appState);
    }
  }, []);

  return {
    ...onboarding,
    getActiveForScope,
    getTourStep,
    getFases,
    dismiss,
    executeAction,
  };
}
